"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import getUserNotificationCounts from "@/api/requests/getUserNotificationCounts";
import useCoreWebVitals from "@/hooks/useCoreWebVitals";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useGoogleAnalyticsUserLocation } from "@/hooks/useGoogleAnalyticsUserLocation";
import { useIFrameAccessibilityFix } from "@/hooks/useIFrameAccessibilityFix";
import { createLoadCookiesAction } from "@/redux/action-creators/cookies";
import { loadUser } from "@/redux/action-creators/user";
import { useDispatch, useSelector } from "@/redux/hooks";
import {
  CREATE_CART_SESSION_ID,
  CreateCartSessionIdAction,
} from "@/redux/reducers/cart";
import { getIsLoggedIn } from "@/redux/selectors/user";
import {
  disableLoadingSpinner,
  updateNotificationCount,
} from "@/redux/slices/header";
import { setLoggedOutRedirect } from "@/redux/slices/user";

/**
 * This component encapsulates side-effects that the app requires in a leaf
 * node, in order to prevent unnecessarily re-rendering any of the application
 * when the dependent state changes. If this code was in the App component
 * itself, changes to the redux state would trigger a re-render of anything
 * rendered by App.
 */
export const AppSideEffects = () => {
  const countryCode = useDomainCountryCode();
  const dispatch = useDispatch();
  const path = usePathname();
  const query = useSearchParams();
  const isLoggedIn = useSelector(getIsLoggedIn);

  useCoreWebVitals();
  useIFrameAccessibilityFix();
  useGoogleAnalyticsUserLocation();

  useEffect(() => {
    dispatch(createLoadCookiesAction());
    dispatch<CreateCartSessionIdAction>({
      countryCode,
      type: CREATE_CART_SESSION_ID,
    });
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    loadUser(countryCode, dispatch);

    getUserNotificationCounts({ countryCode }).then((counts) => {
      dispatch(updateNotificationCount(counts?.unread || 0));
    });
  }, [isLoggedIn, countryCode]);

  useEffect(() => {
    if (query?.has("sign_out_redirect")) {
      dispatch(setLoggedOutRedirect(true));
    }
    dispatch(disableLoadingSpinner());
  }, [path]);

  return null;
};
